import React, { useEffect, useState } from 'react';
import { Popup, Toast } from 'antd-mobile';
import { UpOutline, DownOutline, AddCircleOutline, MinusCircleOutline } from 'antd-mobile-icons';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// 配置 PDF.js 的 worker 文件（版本号带上避免缓存旧版本）
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.5.4.296.js`;

const controlWrapperStyle = {
  display: 'flex',
  justifyContent: 'center',
  height: '1rem',
  alignItems: 'center',
  gap: '0.4rem',
  margin: '0.3rem 1rem',
  padding: '0 0.6rem',
  background: '#444',
  borderRadius: '0.5rem'
};

const controlBtnStyle = {
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  height: '0.6rem',
  alignItems: 'center'
};

export default function PDFViewerMobile({ fileUrl, visible, onClose, fileName }) {
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [scale, setScale] = useState(1.1);
  const [pageWidth, setPageWidth] = useState(null);
  const [pdfSrc, setPdfSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(null);

  useEffect(() => {
    setPageNumber(1);
    setScale(1.1);
    setError(null);
  }, [fileUrl, visible]);

  useEffect(() => {
    let revokedUrl = null;
    const loadPdf = async () => {
      if (!fileUrl || !visible) return;
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(fileUrl, { credentials: 'include', mode: 'cors' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        revokedUrl = URL.createObjectURL(blob);
        setPdfSrc(revokedUrl);
      } catch (err) {
        console.error('[PDFViewerMobile] 获取 PDF 失败', err);
        setError(err?.message || '加载失败');
      } finally {
        setLoading(false);
      }
    };
    loadPdf();
    return () => {
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [fileUrl, visible]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    if (containerWidth) {
      setPageWidth(containerWidth - 32); // 留出左右 padding
    }
  };

  const lastPage = () => {
    if (pageNumber === 1) {
      Toast.show({ content: '已是第一页' });
      return;
    }
    setPageNumber(pageNumber - 1);
  };

  const nextPage = () => {
    if (pageNumber === numPages) {
      Toast.show('已是最后一页');
      return;
    }
    setPageNumber(pageNumber + 1);
  };

  const pageZoomOut = () => {
    if (scale <= 0.3) {
      Toast.show('已缩放至最小');
      return;
    }
    setScale(scale - 0.1);
  };

  const pageZoomIn = () => {
    if (scale >= 5) {
      Toast.show('已放大至最大');
      return;
    }
    setScale(scale + 0.1);
  };
  // 跟随容器宽度自适应
  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(window.innerWidth);
      if (pageWidth && window.innerWidth) {
        setPageWidth(window.innerWidth - 32);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDownload = () => {
    if (!fileUrl) return;
    const link = document.createElement('a');
    link.href = fileUrl;
    const name = fileName || '文件';
    link.download = name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Popup
      position="right"
      visible={visible}
      showCloseButton
      bodyStyle={{ width: '100%' }}
      destroyOnClose
      onClose={onClose}
    >
      <div style={{ padding: '0.42rem 1rem', fontSize: '1rem', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {fileName || 'PDF 预览'}
      </div>
      <div style={{ textAlign: 'right', padding: '0 1rem 0.3rem', fontSize: '1rem', color: '#0B75FF', lineHeight: 1.2 }} onClick={handleDownload}>下载</div>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {fileUrl ? (
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
            {loading && <p style={{ textAlign: 'center' }}>正在加载...</p>}
            {error && <p style={{ textAlign: 'center', color: '#ff4d4f' }}>加载失败：{error}</p>}
            {!loading && !error && (
              <Document
                file={pdfSrc || fileUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={(err) => {
                  console.error('[PDFViewerMobile] 渲染失败', err);
                  setError(err?.message || '渲染失败');
                }}
                options={{ withCredentials: true }}
              >
                <Page pageNumber={pageNumber} width={pageWidth || undefined} scale={pageWidth ? undefined : scale} />
              </Document>
            )}
          </div>
        ) : (
          <p>没有选择文件</p>
        )}
        <div style={{ ...controlWrapperStyle, flexShrink: 0 }}>
          <div style={controlBtnStyle} onClick={lastPage}><UpOutline color="#fff" fontSize="0.6rem" /></div>
          <div style={{ color: '#fff', fontSize: '0.45rem', ...controlBtnStyle }}>{pageNumber}/{numPages}</div>
          <div style={controlBtnStyle} onClick={nextPage}><DownOutline color="#fff" fontSize="0.7rem" /></div>
          <div style={controlBtnStyle} onClick={pageZoomIn}><AddCircleOutline color="#fff" fontSize="0.7rem" /></div>
          <div style={controlBtnStyle} onClick={pageZoomOut}><MinusCircleOutline color="#fff" fontSize="0.7rem" /></div>
        </div>
      </div>
    </Popup>
  );
}
